/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { openPatientSummary } from 'app/pp/components/summary/patient-summary';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { MemberService, MemberType } from 'app/cd/services/member-service';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import { NamespaceOptions } from 'app/shared/lib/i18n';

export const requiredIncludes = [
    'primaryPlan',
    'primaryPlan.healthPlan',
    'primaryPlan.lineOfBusiness',
];

export const requiredFields = [
    'age.years',
    'birthDate',
    'description',
    'eligibility.isCurrent',
    'identifier.primary',
    'isUnknown.name.*',
    'name.first',
    'name.full',
    'name.last',
    'name.middle',
    'primaryPlan.eligibility.isCurrent',
    'primaryPlan.healthPlan.description',
    'primaryPlan.identifier.primary',
    'primaryPlan.lineOfBusiness.description',
    'unknown.displayIdentifier',
    'unknown.name.full'
];

export const requiredPlanFields = [
    'eligibility.isCurrent',
    'healthPlan.description',
    'lineOfBusiness.description',
    'identifier.primary'
];



@autoinject()
export class MemberDisplay extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView })
    public memberId: string = '';
    /**
     * A standard CD.Member object, with additional UM Auth-specific properties tacked on to
     * handle specific plans or unknown (non-network) members */
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public member: CD.Member & Pick<UM.AuthMemberPlanModel, 'plan' | 'unknown'> | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public plan: CD.MemberPlan | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public showIdentifier: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public lastFirst: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public showTooltip: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public showWarning: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    protected useMultiline: boolean = true;

    protected i18nNamespace: Extract<NamespaceOptions, NamespaceOptions.CD | NamespaceOptions.PP> = NamespaceOptions.CD;
    protected memberType: MemberType = MemberType.Member;
    protected memberDisplay: string | null = null;
    protected identifierDisplay: string | null = null;
    protected isEligible: boolean = false;

    protected showMemberSummaryLink: boolean = false;
    protected openPatientSummary = openPatientSummary;
    protected openMemberSummary = openMemberSummary;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private memberService: MemberService,
        ...rest: any[]) {

        super(...rest);

        this.checkPermissions();

        if(this.env.application.code === 'Portal.Providers') {
            this.memberType = MemberType.Patient;
            this.i18nNamespace = NamespaceOptions.PP;
        }
    }

    protected bind(): void {
        this.reload();
    }

    protected detached(): void {
        this.clear();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        // [EN] Note: Member object may be loaded as a binding due to non-network members missing an id.
        if(!this.member) await this.loadMember();

        if(!this.member && !this.plan) {
            this.clear();
            return;
        }

        if(this.member && !this.plan) {
            this.plan = this.member.plan || this.member.primaryPlan || null;
        } else if(this.plan && !this.member) {
            this.member = this.plan.member || null;
        }
        this.refreshMemberDisplay();
    }

    ////****************** Functions ************************************************////
    public async setMember(member: CD.Member): Promise<void> {
        this.member = member;
        await this.loadMember();
        this.plan = this.member.plan || this.member.primaryPlan || null;
        this.refreshMemberDisplay();
    }

    public async setPlan(plan: CD.MemberPlan): Promise<void> {
        this.plan = plan;
        this.memberId = plan.member?.id || '';
        this.member = plan.member || null;
        this.refreshMemberDisplay();
    }

    private refreshMemberDisplay(): void {
        this.isEligible = this.plan?.eligibility?.isCurrent ?? this.member?.eligibility?.isCurrent ?? false;

        const name = !!this.member && !this.member.isUnknown ? this.member.name : this.member?.unknown?.name;
        if(!name) {
            this.logger.warn('No name found on member object');
            return;
        }

        this.identifierDisplay =
            this.plan?.identifier?.primary ||
            this.member?.identifier?.primary ||
            this.member?.unknown?.displayIdentifier ||
            null;

        if(this.lastFirst && name?.last && name?.first) {
            this.memberDisplay = name.last + ', ' + name.first;
        }
        else {
            this.memberDisplay = name?.full || '';
        }

        if(this.showIdentifier) {
            this.memberDisplay += ` (${this.identifierDisplay})`;
        }
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        switch(this.memberType) {
            case MemberType.Patient:
                this.showMemberSummaryLink = this.accountContext.hasRouteAccess('pp.patient.summary');
                break;
            case MemberType.Member:
            default:
                this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
                break;
        }
    }

    public clear(): void {
        this.member = null;
        this.memberId = '';
        this.plan = null;
        this.memberDisplay = null;
        this.identifierDisplay = null;
        this.isEligible = false;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMember(): Promise<void> {
        if(!this.memberId) return;

        try {
            this.state.beginLoad();

            const includes: any[] = [
                'primaryPlan',
                'primaryPlan.healthPlan',
                'primaryPlan.lineOfBusiness',
            ];

            const fields = [
                'age.years',
                'birthDate',
                'description',
                'eligibility.isCurrent',
                'identifier.primary',
                'name.first',
                'name.full',
                'name.last',
                'name.middle',
                'primaryPlan.eligibility.isCurrent',
                'primaryPlan.healthPlan.description',
                'primaryPlan.identifier.primary',
                'primaryPlan.lineOfBusiness.description'
            ];

            const response = await this.memberService.loadMember(this.memberId, includes, fields);

            this.member = response.results[0] || null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }

    }
}
